import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { SERVICES } from "../data";
import "../styles/Home.css";
import WorkVideoModal from "../components/WorkVideoModal";

import g1 from "../assets/gallary1.png";
import g2 from "../assets/gallary2.png";
import g3 from "../assets/gallary3.png";
import g4 from "../assets/gallary4.png";
import g5 from "../assets/gallary5.png";

const photos = [
  { src: g1, alt: "Premium foam wash" },
  { src: g2, alt: "Interior detailing finish" },
  { src: g3, alt: "Engine bay cleaning" },
  { src: g4, alt: "Engine bay cleaning" },
  { src: g5, alt: "Full exterior detailing" },
];

const brands = [
  "maruti-suzuki", "hyundai", "tata", "mahindra", "kia", "toyota", "honda",
  "renault", "nissan", "skoda", "volkswagen", "mg", "jeep", "isuzu",
  "audi", "bmw", "mercedes", "jaguar", "land-rover", "volvo",
  "porsche", "fiat"
];

const workVideos = [
  { src: "/assets/home-video1.mp4", caption: "Premium Foam Wash Cleaning" },
  { src: "/assets/home-video2.mp4", caption: "Exterior and Interior Detailing" },
  { src: "/assets/home-video3.mp4", caption: "Performance Staging & Tuning" },
  { src: "/assets/home-video4.mp4", caption: "Full Car Body Rework & Refinishing" }
];

function useWindowSize() {
  const [size, setSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768
  });

  useEffect(() => {
    const handleResize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return size;
}

function usePageVisibility() {
  const [visible, setVisible] = useState(
    typeof document !== 'undefined' ? !document.hidden : true
  );

  useEffect(() => {
    const handleVisibilityChange = () => {
      setVisible(!document.hidden);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  return visible;
}

export default function Home() {
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const pageVisible = usePageVisibility();
  
  const [modalVideo, setModalVideo] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isPhotoOpen, setIsPhotoOpen] = useState(false);
  const [lastInteraction, setLastInteraction] = useState(Date.now());
  const [heroMuted, setHeroMuted] = useState(true);
  
  const photoTimerRef = useRef();
  const keydownHandlerRef = useRef();
  const heroVideoRef = useRef(null);

  // Animation variants
  const containerVariants = useMemo(() => ({
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.1 }
    }
  }), []);

  const itemVariants = useMemo(() => ({
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0 }
  }), []);

  const markInteraction = useCallback(() => {
    setLastInteraction(Date.now());
  }, []);

  const openPhoto = useCallback((index) => {
    setPhotoIndex(index);
    setIsPhotoOpen(true);
    markInteraction();
  }, [markInteraction]);

  const closePhoto = useCallback(() => {
    setIsPhotoOpen(false);
    if (photoTimerRef.current) {
      clearInterval(photoTimerRef.current);
      photoTimerRef.current = null;
    }
    markInteraction();
  }, [markInteraction]);

  const updatePhotoIndex = useCallback((newIndex) => {
    setPhotoIndex(newIndex);
    markInteraction();
  }, [markInteraction]);

  const prevPhoto = useCallback(() => {
    updatePhotoIndex((photoIndex - 1 + photos.length) % photos.length);
  }, [photoIndex, updatePhotoIndex]);

  const nextPhoto = useCallback(() => {
    updatePhotoIndex((photoIndex + 1) % photos.length);
  }, [photoIndex, updatePhotoIndex]);

  // Hero video mute toggle
  const toggleHeroMute = useCallback((e) => {
    e.stopPropagation();
    const newMutedState = !heroMuted;
    setHeroMuted(newMutedState);
    if (heroVideoRef.current) {
      heroVideoRef.current.muted = newMutedState;
    }
  }, [heroMuted]);

  // Keyboard navigation
  useEffect(() => {
    if (!isPhotoOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closePhoto();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevPhoto();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        nextPhoto();
      }
    };

    keydownHandlerRef.current = handleKeyDown;
    window.addEventListener("keydown", handleKeyDown);
    
    return () => {
      if (keydownHandlerRef.current) {
        window.removeEventListener("keydown", keydownHandlerRef.current);
      }
    };
  }, [isPhotoOpen, closePhoto, prevPhoto, nextPhoto]);

  // Auto-advance photos
  useEffect(() => {
    if (!isPhotoOpen || !pageVisible) return;

    const now = Date.now();
    if (now - lastInteraction < 3000) return;

    photoTimerRef.current = setInterval(() => {
      nextPhoto();
    }, 6000);

    return () => {
      if (photoTimerRef.current) {
        clearInterval(photoTimerRef.current);
        photoTimerRef.current = null;
      }
    };
  }, [isPhotoOpen, pageVisible, lastInteraction, nextPhoto]);

  const openService = useCallback((slug) => {
    navigate(`/services?highlight=${slug}`);
  }, [navigate]);

  const isMobile = width < 768;

  return (
    <section className="container page home" onPointerDown={markInteraction}>
      <div className="hero">
        <div className="hero-left">
          <motion.h1
            className="gradient-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Premium Car Care in Chennai
          </motion.h1>

          <motion.p
            className="muted"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Trusted technicians, transparent pricing and best-in-class service.
          </motion.p>

          <div className="actions">
            <Link to="/services" className="btn secondary">
              View Services
            </Link>
            <Link to="/book" className="btn">
              Book Now
            </Link>
          </div>
        </div>

        <div className="hero-right">
          <div className="hero-video-wrapper">
            <video
              ref={heroVideoRef}
              className="hero-video"
              autoPlay
              muted={heroMuted}
              loop
              playsInline
              preload="metadata"
              poster="/assets/hero-poster.jpg"
            >
              <source src="/assets/hero.mp4" type="video/mp4" />
            </video>
            <div className="hero-video-caption">
              🚗 Shine like new!
              <span className="muted">
                {isMobile ? "Mobile Optimized Care" : "Your car deserves the best."}
              </span>
            </div>
            
            {/* Hero Mute Toggle Button */}
            <button
              className="hero-mute-toggle"
              onClick={toggleHeroMute}
              aria-label={heroMuted ? "Unmute video" : "Mute video"}
              title={heroMuted ? "Unmute" : "Mute"}
            >
              {heroMuted ? "🔇" : "🔊"}
            </button>
          </div>
        </div>
      </div>

      <div className="trust-strip">
        <div><strong>15+</strong><span>Years Experience</span></div>
        <div><strong>3000+</strong><span>Cars Serviced</span></div>
        <div><strong>100%</strong><span>Quality Check</span></div>
        <div><strong>Premium</strong><span>Products Used</span></div>
      </div>

      <motion.section
        className="why-us-section"
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <h2 className="section-title gradient-text">Why Choose Us</h2>
        <div className="grid why-us-grid">
          {["Certified Technicians", "Premium Equipment", "Transparent Pricing", 
            "On-Time Delivery", "Genuine Products", "Customer-First Support"].map((text, i) => (
            <motion.div key={text} variants={itemVariants}>{text}</motion.div>
          ))}
        </div>
      </motion.section>

      <motion.section
        className="brands-section"
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <h2 className="section-title gradient-text">Brands We Serve</h2>
        <div className="brands-grid">
          {brands.map((brand) => (
            <motion.div key={brand} variants={itemVariants} className="brand-card">
              <img src={`/assets/brands/${brand}.png`} alt={brand} loading="lazy" />
            </motion.div>
          ))}
        </div>
      </motion.section>

      <motion.section
        className="services-section"
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <h2 className="section-title gradient-text">Popular Services</h2>
        <div className="grid popular-services-grid">
          {SERVICES.slice(0, 6).map((service) => (
            <motion.div
              key={service.slug}
              variants={itemVariants}
              className="popular-service-card"
              role="button"
              tabIndex={0}
              onClick={() => openService(service.slug)}
              onKeyDown={(e) => e.key === 'Enter' && openService(service.slug)}
            >
              <div className="popular-service-img">
                {service.image ? (
                  <img src={service.image} alt={service.title} loading="lazy" />
                ) : (
                  <div className="img-placeholder">🚗</div>
                )}
              </div>
              <h4>{service.title}</h4>
              <p className="muted">
                Premium {service.title.toLowerCase()} service with expert care.
              </p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      <div className="work-videos-section">
        <h2 className="section-title gradient-text">Our Work</h2>
        <div className="work-videos-grid">
          {workVideos.map((video, index) => (
            <div
              key={video.src}
              className="work-video-card"
              role="button"
              tabIndex={0}
              onClick={() => setModalVideo(video)}
              onKeyDown={(e) => e.key === 'Enter' && setModalVideo(video)}
            >
              <video
                className="work-video"
                src={video.src}
                muted
                loop
                playsInline
                preload="metadata"
              />
              <span className="work-video-caption">{video.caption}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="gallery-section">
        <h2 className="section-title gradient-text">Photo Gallery</h2>
        <div className="gallery-grid">
          {photos.map((photo, index) => (
            <div
              key={photo.src}
              className="gallery-item"
              role="button"
              tabIndex={0}
              onClick={() => openPhoto(index)}
              onKeyDown={(e) => e.key === 'Enter' && openPhoto(index)}
            >
              <img src={photo.src} alt={photo.alt} loading="lazy" />
            </div>
          ))}
        </div>
      </div>

      <div className="final-cta">
        <h2>Ready to Transform Your Car?</h2>
        <p className="muted">
          Book a premium car care service today and experience the difference.
        </p>
        <Link to="/book" className="btn">Book Appointment</Link>
      </div>

      <WorkVideoModal
        open={Boolean(modalVideo)}
        onClose={() => setModalVideo(null)}
        videoSrc={modalVideo?.src}
        caption={modalVideo?.caption}
      />

      {isPhotoOpen && (
        <div 
          className="photo-modal-backdrop" 
          role="dialog"
          aria-label="Photo gallery"
          onClick={closePhoto}
        >
          <div 
            className="photo-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="photo-modal-image">
              <img 
                src={photos[photoIndex].src} 
                alt={photos[photoIndex].alt} 
              />
            </div>
            <div className="photo-modal-controls">
              <button 
                className="nav-btn prev" 
                onClick={prevPhoto}
                aria-label="Previous photo"
              >
                ◀
              </button>
              <button 
                className="nav-btn next" 
                onClick={nextPhoto}
                aria-label="Next photo"
              >
                ▶
              </button>
              <button 
                className="close-btn"
                onClick={closePhoto}
                aria-label="Close gallery"
              >
                ×
              </button>
            </div>
            <div className="photo-counter">
              {photoIndex + 1} / {photos.length}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
